import React, { useEffect, useState } from 'react';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { getEmpresas, createEmpresa, updateEmpresa } from '../../api/empresas';
import { formatCnpj, isValidCnpj } from '../../utils/cpf';
import usePaginacao from '../../hooks/usePaginacao';
import Paginacao from '../../components/common/Paginacao';

const EMPTY = { razao_social: '', cnpj: '', nome_fantasia: '' };

export default function Empresas() {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [error, setError] = useState('');

  const load = () => getEmpresas().then(setEmpresas).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidCnpj(form.cnpj)) {
      setError('CNPJ inválido — informe os 14 dígitos');
      return;
    }
    try {
      await createEmpresa({ ...form, cnpj: form.cnpj.replace(/\D/g, '') });
      setForm(EMPTY);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao cadastrar empresa');
    }
  };

  const startEdit = (emp) => {
    setEditId(emp.id_empresa);
    setEditForm({ razao_social: emp.razao_social, nome_fantasia: emp.nome_fantasia || '' });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await updateEmpresa(editId, editForm);
      setEditId(null);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao atualizar empresa');
    }
  };

  const pag = usePaginacao(empresas);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        title="Empresas"
        subtitle="Empresas cadastradas no sistema"
        action={
          <button className="btn-datafit" onClick={() => { setShowForm(!showForm); setEditId(null); }}>
            <i className="bi bi-plus me-2"></i>Nova empresa
          </button>
        }
      />

      {error && (
        <div className="alert alert-danger py-2 small mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>{error}
        </div>
      )}

      {showForm && (
        <div className="card-datafit p-4 mb-4">
          <div className="card-header-datafit mb-3" style={{ borderRadius: '0 12px 12px 0', display: 'inline-block' }}>
            Nova empresa
          </div>
          <form onSubmit={handleCreate}>
            <div className="row g-3">
              <div className="col-md-5">
                <div className="form-floating">
                  <input className="form-control" id="razao" placeholder="Razão social"
                    value={form.razao_social} onChange={set('razao_social')} required />
                  <label htmlFor="razao">Razão social</label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-floating">
                  <input className="form-control" id="cnpj" placeholder="00.000.000/0000-00"
                    value={form.cnpj}
                    onChange={(e) => setForm({ ...form, cnpj: formatCnpj(e.target.value) })}
                    required />
                  <label htmlFor="cnpj">CNPJ</label>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-floating">
                  <input className="form-control" id="fantasia" placeholder="Nome fantasia"
                    value={form.nome_fantasia} onChange={set('nome_fantasia')} />
                  <label htmlFor="fantasia">Nome fantasia</label>
                </div>
              </div>
            </div>
            <div className="d-flex gap-2 mt-3">
              <button type="submit" className="btn-datafit" style={{ height: 40, fontSize: '.85rem' }}>Salvar</button>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowForm(false)}>Cancelar</button>
            </div>
          </form>
        </div>
      )}

      <div className="card-datafit">
        <div className="table-responsive">
          <table className="table table-datafit mb-0">
            <thead>
              <tr><th>Razão social</th><th>CNPJ</th><th>Nome fantasia</th><th></th></tr>
            </thead>
            <tbody>
              {empresas.length === 0 ? (
                <tr><td colSpan={4} className="text-center text-muted py-4">Nenhuma empresa cadastrada</td></tr>
              ) : pag.paginados.map(emp => (
                <React.Fragment key={emp.id_empresa}>
                  <tr>
                    <td className="fw-bold">{emp.razao_social}</td>
                    <td>{emp.cnpj}</td>
                    <td>{emp.nome_fantasia || '—'}</td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary" title="Editar"
                        onClick={() => editId === emp.id_empresa ? setEditId(null) : startEdit(emp)}>
                        <i className="bi bi-pencil"></i>
                      </button>
                    </td>
                  </tr>
                  {editId === emp.id_empresa && (
                    <tr key={`edit-${emp.id_empresa}`} className="table-light">
                      <td colSpan={4} className="p-3">
                        <form onSubmit={handleEdit} className="d-flex gap-2 flex-wrap align-items-end">
                          <div>
                            <label className="form-label small mb-1">Razão social</label>
                            <input className="form-control form-control-sm" style={{ width: 260 }}
                              value={editForm.razao_social}
                              onChange={e => setEditForm({ ...editForm, razao_social: e.target.value })} required />
                          </div>
                          <div>
                            <label className="form-label small mb-1">Nome fantasia</label>
                            <input className="form-control form-control-sm" style={{ width: 200 }}
                              value={editForm.nome_fantasia}
                              onChange={e => setEditForm({ ...editForm, nome_fantasia: e.target.value })} />
                          </div>
                          <button type="submit" className="btn btn-sm btn-primary">Salvar</button>
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setEditId(null)}>Cancelar</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <Paginacao {...pag} />
      </div>
    </>
  );
}
